// HOC forwards all props and is prop-type agnostic
/* eslint-disable react/prop-types, react/jsx-props-no-spreading */
import React from 'react';
import { ThemeProvider } from '@one-thd/sui-atomic-components';
import { getThemeByName } from '@thd-olt-component-react/stencil-themes';

// Wraps a component in a ThemeProvider: theme = props.theme || opts.theme ||
// (props.setThemeFromCms ? props.cmsTheme : none). No theme -> renders untouched.
export function withThemeProvider(Component, opts = {}) {
  function ThemedComponent(props) {
    const { theme, setThemeFromCms, cmsTheme } = props;
    // props.theme wins over opts.theme: withLayout wraps once and freezes opts.theme at
    // that render, so a later theme change (e.g. a new CMS selection) would go stale
    // if opts.theme took priority. props.theme is forwarded fresh on every render.
    const themeName = theme
      || opts.theme
      || (setThemeFromCms ? cmsTheme : undefined);

    if (!themeName) {
      return <Component {...props} />;
    }

    return (
      // ssr: without it, ThemeProvider applies the palette via a client-only useEffect that
      // imperatively styles the parent DOM node - it never runs during server rendering (or
      // before hydration), so the theme is invisible with JS off/delayed, causing a flash of
      // unstyled content once it finally applies. ssr renders the palette as inline styles on
      // a wrapping div instead, so it's present in the initial server-rendered markup. Always
      // passing it (not just on the server) keeps server and client output identical, avoiding
      // a hydration mismatch from switching injection strategies after mount.
      <ThemeProvider theme={getThemeByName(themeName)} ssr>
        <Component {...props} />
      </ThemeProvider>
    );
  }

  ThemedComponent.displayName = `withThemeProvider(${Component.displayName || Component.name || 'Component'})`;
  ThemedComponent.dataModel = Component.dataModel;
  ThemedComponent.wraps = { ...(Component.wraps || {}), themeProvider: true };

  return ThemedComponent;
}
